import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounce } from 'lodash';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  SortDropdownComponent,
  SortDropdownGroup,
  buildSortGroups,
} from '../../../../components/inputs/sort-dropdown/sort-dropdown.component';
import { UsersTableComponent } from '../../../../components/tables/settings/users-table/users-table.component';
import { AddUserFormComponent } from '../../../../components/forms/settingsforms/add-user-form/add-user-form.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { IInstitutionUser } from '../types/SettingsModuleTypes';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { IPagination } from '../../../../types/contactModule';
import { isAdmin, userState } from '../settings-roles';
import { formatCount } from '../../../../../utils/sms';

export type TUserSort = 'firstName,asc' | 'firstName,desc' | 'createdTimestamp,asc' | 'createdTimestamp,desc';

export type TUserFilter = 'all' | 'active' | 'disabled';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

/** One fact in the team summary line. */
interface SummaryPart {
  value: string;
  label: string;
  tone?: 'bad';
}

@Component({
  selector: 'app-user-management-settings',
  standalone: true,
  imports: [
    CommonModule,
    StatusFilterComponent,
    SortDropdownComponent,
    SearchInputComponent,
    UsersTableComponent,
    AddUserFormComponent,
    HasPermissionDirective,
  ],
  templateUrl: './user-management-settings.component.html',
  styleUrls: ['../settings-ui.css', './user-management-settings.component.css'],
})
export class UserManagementSettingsComponent implements OnInit, OnDestroy {
  constructor() {
    // Bind the debounced function
    this.debouncedSearch = debounce(this.searchUsers.bind(this), 400);
  }

  showAddUser = false;
  settingsService = inject(SettingsService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  allUsers: IInstitutionUser[] = [];
  pagination: IPagination | null = null;
  /** False until the first list arrives, so the summary never reads "0 people" while loading. */
  usersLoaded = false;
  /** The first list failed to load; the table shows its empty state instead of a skeleton. */
  usersFailed = false;
  searchTerm: string = '';
  sortCriteria: TUserSort = 'firstName,asc';
  filterCriteria: TUserFilter = 'all';

  // Feedback goes to the toast rail
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;

  readonly sortGroups: SortDropdownGroup[] = buildSortGroups({
    nameField: 'firstName',
    createdField: 'createdTimestamp',
  });

  readonly filterOptions: StatusFilterOption[] = [
    { value: 'all', title: 'Everyone', icon: 'group', tone: 'neutral' },
    { value: 'active', title: 'Enabled', icon: 'radio_button_checked', tone: 'success' },
    // Grey, like the Disabled dot in the table: a state, not a failure.
    { value: 'disabled', title: 'Disabled', icon: 'block', tone: 'neutral' },
  ];

  // params
  params: Record<string, any> = { sort: 'firstName,asc', size: 10 };

  // Debounced method
  debouncedSearch!: (...args: any[]) => void;

  get reactiveParams() {
    return this.params;
  }

  /** Skeleton for the first load only; later refreshes keep the rows on screen. */
  get tableLoading(): boolean {
    return !this.usersLoaded && !this.usersFailed;
  }

  get isFiltered(): boolean {
    return this.filterCriteria !== 'all';
  }

  /** Every member is on screen: no search, no filter, one page. */
  get listIsComplete(): boolean {
    const total = this.pagination?.totalElements ?? this.allUsers.length;
    return !this.searchTerm.trim() && !this.isFiltered && this.allUsers.length >= total;
  }

  /** "12 people · 3 administrators · 1 invite pending · 2 disabled". */
  get summary(): SummaryPart[] {
    if (!this.usersLoaded) return [];
    const total = this.pagination?.totalElements ?? this.allUsers.length;

    if (this.searchTerm.trim() || this.isFiltered) {
      return [{ value: formatCount(total), label: total === 1 ? 'person matches' : 'people match' }];
    }

    const parts: SummaryPart[] = [{ value: formatCount(total), label: total === 1 ? 'person' : 'people' }];
    if (!this.listIsComplete) {
      // Breakdowns would only describe this page; say so instead of guessing.
      parts.push({ value: formatCount(this.allUsers.length), label: 'on this page' });
      return parts;
    }

    const admins = this.allUsers.filter((user) => user.enabled && isAdmin(user.realmRoles)).length;
    const pending = this.allUsers.filter((user) => userState(user) === 'pending').length;
    const disabled = this.allUsers.filter((user) => userState(user) === 'disabled').length;
    parts.push({ value: formatCount(admins), label: admins === 1 ? 'administrator' : 'administrators' });
    if (pending) parts.push({ value: formatCount(pending), label: pending === 1 ? 'invite pending' : 'invites pending' });
    if (disabled) parts.push({ value: formatCount(disabled), label: 'disabled' });
    return parts;
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    if (term.trim().length < 1) {
      delete this.params['firstName.contains'];
      delete this.params['lastName.contains'];
      delete this.params['email.contains'];
    } else {
      // The IAM list filters on first name only.
      this.params['firstName.contains'] = term.trim();
    }
    delete this.params['page'];
    this.debouncedSearch();
  }

  searchUsers() {
    this.settingsService.fetchInstitutionUsers(this.params);
  }

  updateSortCriteria(criteria: TUserSort) {
    this.sortCriteria = criteria;
    this.params['sort'] = criteria;
    this.settingsService.fetchInstitutionUsers(this.params);
  }

  updateFilterCriteria(filter: TUserFilter) {
    this.filterCriteria = filter || 'all';

    // Update enabled parameter based on filter
    if (this.filterCriteria === 'active') {
      this.params['enabled'] = true;
    } else if (this.filterCriteria === 'disabled') {
      this.params['enabled'] = false;
    } else {
      delete this.params['enabled'];
    }
    delete this.params['page'];

    this.settingsService.fetchInstitutionUsers(this.params);
  }

  ngOnInit() {
    // Defer the initial fetch to a microtask so the loading flags flip in
    // their own change-detection cycle (avoids NG0100).
    Promise.resolve().then(() => {
      this.settingsService.fetchInstitutionUsers(this.params);
      this.settingsService.fetchUserRoles();
    });

    this.settingsService.getInstitutionUsers.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: IInstitutionUser[]) => {
        this.allUsers = Array.isArray(res) ? res : [];
      },
    });

    // The subject replays its last value on subscribe: a placeholder on a first
    // visit, a real answer when returning. Only real answers flip usersLoaded.
    let replay = true;
    this.settingsService.getInstitutionUsersPagination
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pagination: IPagination) => {
        this.pagination = pagination;
        if (!replay || this.allUsers.length > 0) this.usersLoaded = true;
      });
    replay = false;

    // Surface fetch failures for users/roles
    this.settingsService.onErrorMessage.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ message, source }) => {
      if (source === 'fetchInstitutionUsers') {
        this.usersFailed = !this.usersLoaded;
        this.displayFeedback(`The team list did not load. ${message}`, 'error');
      } else if (source === 'fetchUserRoles') {
        this.displayFeedback(`The list of roles did not load. ${message}`, 'error');
      }
    });
  }

  /** Try the first load again from the table's load-failed state. */
  retryUsers() {
    this.usersFailed = false;
    this.settingsService.fetchInstitutionUsers(this.params);
  }

  onAddUserSuccess() {
    this.displayFeedback('User added. They will get an email to set a password.', 'success');
    // The service already refreshes the list with these criteria after a create.
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  ngOnDestroy() {
    (this.debouncedSearch as any)?.cancel?.();
  }
}
